import Drawer from "common/Drawers/Drawer.tsx";
import PublicBotStatistics from "features/bots/PublicBots/PublicBotStatistics.tsx";
import { BotGraphType, BotTemplateData, WorkspaceType } from "@shared/types";
import { usePublicBotPage } from "@shared/features/bots/hooks.ts";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import BotChart from "features/bots/common/BotChart.tsx";
import { ICON_TYPES, TIME_RANGE_START } from "@shared/common/constants.ts";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import styled, { useTheme } from "styled-components";
import Tabs from "common/Tabs/Tabs.tsx";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import Flex from "common/Boxes/Flex.tsx";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import BotConfigurationSummary from "features/bots/BotDrawer/BotSummary.tsx";
import React, { MouseEvent } from "react";
import Button from "common/Buttons/Button.tsx";
import IconWithFallback from "Global/IconWithFallback";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_GAP,
  newLookType,
} from "common/NewLook/tokens.ts";
import { newLookChip } from "features/bots/NewLook/newLookStyles";
import NewLookPublicBotPerformance from "features/bots/PublicBots/NewLookPublicBotPerformance.tsx";
import { getPublicBotChips } from "features/bots/PublicBots/publicBotDisplay";
import {
  BotSub,
  BotSubDot,
  BotSubPart,
} from "features/bots/PublicBots/newLookPublicBotStyles";

interface PublicBotDrawerProps {
  open: boolean;
  closeDrawer: () => void;
  botData: BotTemplateData;
  /** New look: the drawer's one action, copying the bot into the form. */
  onCopy?: (e: MouseEvent) => void;
}

const PublicBotDrawer = (props: PublicBotDrawerProps) => {
  const { isNewLook } = useTheme();
  if (isNewLook) return <NewLookPublicBotDrawer {...props} />;
  return <ClassicPublicBotDrawer {...props} />;
};

/**
 * New look: the bot's name and market as the drawer's title, its results as
 * the overview panel (headline, chart, key figures), its settings as the
 * bot drawer's section panels, and "Copy Bot" as the footer's one action.
 */
const NewLookPublicBotDrawer = ({
  open,
  closeDrawer,
  botData,
  onCopy,
}: PublicBotDrawerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const tokens = getNewLookTokens(useTheme());
  const isDemo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const { getBotDataStatus } = usePublicBotPage(botData.uuid, isDemo);
  const symbol = botData.symbols?.[0];

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      destroyOnHidden={true}
      styles={{
        body: { padding: "20px 24px 24px" },
        footer: {
          display: "flex",
          justifyContent: "flex-end",
          padding: "12px 24px",
          borderTop: `1px solid ${tokens.hairline}`,
          background: tokens.panel,
        },
      }}
      footer={
        onCopy && (
          <CopyButton
            type="primary"
            onClick={(e: MouseEvent) => {
              closeDrawer();
              onCopy(e);
            }}
          >
            {t("public_bots.copy_bot")}
          </CopyButton>
        )
      }
    >
      <Stack>
        <Header>
          <IconWithFallback
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={symbol}
            iconStyle={ICON_STYLE}
          />
          <HeaderText>
            <DrawerTitle title={botData.name}>{botData.name}</DrawerTitle>
            <BotSub>
              {[symbol, botData.exchange].filter(Boolean).map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <BotSubDot />}
                  <BotSubPart title={part}>{part}</BotSubPart>
                </React.Fragment>
              ))}
            </BotSub>
          </HeaderText>
          <Chips>
            {getPublicBotChips(botData).map((chip) => (
              <Chip key={chip}>{chip}</Chip>
            ))}
          </Chips>
        </Header>
        <NewLookPublicBotPerformance
          botUUID={botData.uuid}
          botData={botData}
          created={getBotDataStatus.data?.created_at}
          isDemoCopyPublic={isDemo}
          stacked={true}
        />
        {getBotDataStatus.data ? (
          <BotConfigurationSummary
            botData={getBotDataStatus.data}
            style={CONFIGURATION_STYLE}
          />
        ) : (
          <Panel>
            <ConfigurationSkeleton aria-busy="true">
              {[0, 1, 2, 3].map((index) => (
                <SkeletonRow key={index}>
                  <Skeleton width="30%" height={12} />
                  <Skeleton width="40%" height={12} />
                </SkeletonRow>
              ))}
            </ConfigurationSkeleton>
          </Panel>
        )}
      </Stack>
    </Drawer>
  );
};

const ClassicPublicBotDrawer = ({
  open,
  closeDrawer,
  botData,
}: PublicBotDrawerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const workspace = useSelector(selectWorkspace);
  const isDemo = workspace === WorkspaceType.DEMO;
  const { chartTab, setChartTab, getBotDataStatus } = usePublicBotPage(
    botData.uuid,
    isDemo
  );

  const accumulatedProfitChart = (
    <BotChart
      publicBotUUID={botData.uuid}
      chartType={BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE}
      defaultTimeRange={TIME_RANGE_START}
      isDemoCopyPublic={isDemo}
      mobileBreakpoint={Infinity}
    />
  );

  const profitByDayChart = (
    <BotChart
      publicBotUUID={botData.uuid}
      chartType={BotGraphType.PROFIT_BY_DAY_PERCENTAGE}
      defaultTimeRange={TIME_RANGE_START}
      isDemoCopyPublic={isDemo}
      mobileBreakpoint={Infinity}
    />
  );

  const graphTabsItems = [
    {
      key: BotGraphType.ACCUMULATING_PROFIT.toString(),
      label: t(BotGraphTypeToDisplay[BotGraphType.ACCUMULATING_PROFIT]),
      children: <ChartWrapper>{accumulatedProfitChart}</ChartWrapper>,
    },
    {
      key: BotGraphType.PROFIT_BY_DAY.toString(),
      label: t(BotGraphTypeToDisplay[BotGraphType.PROFIT_BY_DAY]),
      children: <ChartWrapper>{profitByDayChart}</ChartWrapper>,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      styles={{ body: { padding: "16px 24px 24px" } }}
      destroyOnHidden={true}
    >
      <LoadingSpinnerOverlay isLoading={getBotDataStatus.isFetching}>
        <Flex $justifyContent="center" style={{ marginBottom: 32 }}>
          <TextWithIcon
            text={botData.name}
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={botData.symbols[0]}
            iconSize="xxl"
            textStyle={{
              color: theme.colors.primaryText,
              fontSize: theme.typography.fontSizes.L,
            }}
          />
        </Flex>
        <PublicBotStatistics
          botData={botData}
          created={getBotDataStatus?.data?.created_at}
        />
        <Tabs
          activeKey={chartTab.toString()}
          onTabClick={(tabKey) => setChartTab(parseInt(tabKey))}
          size="small"
          $hideBorder={true}
          items={graphTabsItems}
          style={{ margin: "24px 0" }}
        />
        {getBotDataStatus.data && (
          <BotConfigurationSummary botData={getBotDataStatus.data} />
        )}
      </LoadingSpinnerOverlay>
    </Drawer>
  );
};

export default PublicBotDrawer;

const ICON_STYLE = { width: 36, height: 36, marginRight: 0, flex: "none" };
const CONFIGURATION_STYLE = { paddingBottom: 0 };

const ChartWrapper = styled.div`
  height: 290px;
  width: 100%;
`;
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_GAP}px;
`;
// Leaves room on the right for the drawer's close button.
const Header = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;
  padding-right: 40px;
`;
const HeaderText = styled.div`
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const DrawerTitle = styled.h2`
  margin: 0;
  overflow-wrap: anywhere;
  ${newLookType.pageTitle};
  font-size: 18px;
  line-height: 24px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const Chip = styled.span`
  ${newLookChip};
`;
const CopyButton = styled(Button)`
  @media (max-width: 600px) {
    width: 100%;
  }
`;
const ConfigurationSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
`;
